import { LanguageSupport, syntaxHighlighting, indentUnit, foldGutter, bracketMatching } from '@codemirror/language';
import { EditorView, keymap, lineNumbers, highlightActiveLineGutter, highlightActiveLine } from '@codemirror/view';
import { indentWithTab, history, historyKeymap, redo, toggleBlockComment, selectAll } from '@codemirror/commands';
import { closeBrackets, closeBracketsKeymap, autocompletion, CompletionContext, type CompletionResult, completionKeymap } from '@codemirror/autocomplete';
import { toValue, type MaybeRefOrGetter } from 'vue';
import { JSONATA_COMPLETIONS } from '../constants/jsonata-completions';
import { customHighlightStyle } from '../constants/code-editor-styles';
import { jsonataStreamLanguage, jsonataFoldService } from '../constants/jsonata-language';
import type { WorkflowRuntimeConfig } from '../types/workflows';

export function useJsonataEditor (options?: { runtimeConfig?: MaybeRefOrGetter<WorkflowRuntimeConfig | undefined> }) {
    const gutters = [lineNumbers(), highlightActiveLineGutter(), highlightActiveLine()];

    const mapStaticCompletions = () => JSONATA_COMPLETIONS.map(item => ({
        label: item.label,
        type: item.type,
        info: item.info,
        section: item.category,
        ...(item.type === 'function' && {
            apply: (view: EditorView, _completion: unknown, from: number, to: number) => {
                const text = item.label + '()';
                view.dispatch({
                    changes: { from, to, insert: text },
                    selection: { anchor: from + text.length - 1 }
                });
            }
        })
    }));

    const mapDynamicCompletions = () => {
        const runtimeConfig = toValue(options?.runtimeConfig);
        const aliases = runtimeConfig?.connectionAliases ?? [];
        const variableKeys = Array.from(new Set(
            Object.values(runtimeConfig?.environments ?? {}).flatMap(e => Object.keys(e.variables ?? {}))
        ));
        return {
            aliases,
            variableKeys,
            options: [
                ...aliases.map(alias => ({
                    label: `$context.runtime.connections.${alias}`,
                    type: 'variable' as const,
                    info: `Connection ID resolved for alias "${alias}"`,
                    section: 'Context'
                })),
                ...variableKeys.map(key => ({
                    label: `$context.runtime.variables.${key}`,
                    type: 'variable' as const,
                    info: `Runtime variable "${key}"`,
                    section: 'Context'
                }))
            ]
        };
    };

    // Create autocompletion
    const jsonataAutocompletion = autocompletion({
        override: [
            (context: CompletionContext): CompletionResult | null => {
                const word = context.matchBefore(/\$[\w.]*/);
                if (!word) {
                    // Fallback for non-$ tokens (plain identifiers)
                    const fallback = context.matchBefore(/\$?\w*/);
                    if (!fallback) { return null; }
                    if (fallback.from === fallback.to && !context.explicit) { return null; }
                    return { from: fallback.from, options: mapStaticCompletions() };
                }

                const textBefore = word.text;
                const dynamic = mapDynamicCompletions();

                // Scoped completions when typing a specific runtime path
                if (textBefore.startsWith('$context.runtime.connections.')) {
                    return { from: word.from, options: dynamic.options.filter(o => o.label.startsWith('$context.runtime.connections.')) };
                }
                if (textBefore.startsWith('$context.runtime.variables.')) {
                    return { from: word.from, options: dynamic.options.filter(o => o.label.startsWith('$context.runtime.variables.')) };
                }

                return {
                    from: word.from,
                    options: [...mapStaticCompletions(), ...dynamic.options]
                };
            }
        ]
    });

    const customEnterCommand = (view: EditorView) => {
        const state = view.state;
        const pos = state.selection.main.head;
        const line = state.doc.lineAt(pos);
        const indent = line.text.match(/^[\t ]*/)?.[0] ?? '';
        view.dispatch({
            changes: { from: pos, to: pos, insert: '\n' + indent },
            selection: { anchor: pos + 1 + indent.length }
        });
        return true;
    };

    const customKeymap = keymap.of([
        { key: 'Enter', run: customEnterCommand, preventDefault: true },
        indentWithTab,
        ...historyKeymap, // This includes standard undo/redo bindings
        { key: 'Ctrl-Shift-z', run: redo, preventDefault: true }, // Windows/Linux
        { key: 'Cmd-Shift-z', run: redo, preventDefault: true }, // Mac
        { key: 'Shift-Alt-a', run: toggleBlockComment, preventDefault: true },
        { key: 'Mod-a', run: selectAll, preventDefault: true }
    ]);

    // Create the language support
    function createJsonataLanguage () {
        return new LanguageSupport(
            jsonataStreamLanguage,
            [
                jsonataAutocompletion,
                syntaxHighlighting(customHighlightStyle),
                customKeymap,
                keymap.of([indentWithTab]),
                indentUnit.of('    '),
                history(),
                foldGutter(),
                jsonataFoldService,
                closeBrackets(),
                keymap.of(closeBracketsKeymap),
                keymap.of(completionKeymap),
                bracketMatching(),
                ...gutters
            ]
        );
    }

    const editorKeymap = keymap.of([
        indentWithTab,
        ...historyKeymap,
        { key: 'Ctrl-Shift-z', run: redo, preventDefault: true },
        { key: 'Cmd-Shift-z', run: redo, preventDefault: true },
        { key: 'Shift-Alt-a', run: toggleBlockComment, preventDefault: true }
    ]);

    // Return the language support and shared theme
    return {
        jsonataLanguage: createJsonataLanguage(),
        tabKeymap: editorKeymap,
        indentConfig: indentUnit.of('    ')
    };
}
